import { ButtonDesignTokens } from '@primeuix/themes/aura/button';

export const buttonTokens: ButtonDesignTokens = {
  root: {
    borderRadius: '{form.field.border.radius}',
    roundedBorderRadius: '2rem',
    gap: '0.5rem',
    paddingX: '{form.field.padding.x}',
    paddingY: '0.5rem',
    iconOnlyWidth: '2.5rem',
    sm: {
      fontSize: '0.875rem',
      paddingX: '0.625rem',
      paddingY: '0.3125rem',
      iconOnlyWidth: '2rem',
    },
    lg: {
      fontSize: '1.125rem',
      paddingX: '0.875rem',
      paddingY: '0.625rem',
      iconOnlyWidth: '3rem',
    },
    label: {
      fontWeight: '500',
    },
    raisedShadow: '0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)',
    focusRing: {
      width: '{focus.ring.width}',
      offset: '{focus.ring.offset}',
      style: '{focus.ring.style}',
    },
    badgeSize: '1.5rem',
    transitionDuration: '{form.field.transition.duration}',
  },
  extend: {
    size: {
      lineHeight: '1.375rem',
      sm: {
        lineHeight: '1.25rem',
      },
      lg: {
        lineHeight: '1.5rem',
      },
    },
    badge: {
      fontSize: '0.875rem',
      borderRadius: '1rem',
      sm: {
        size: '1.25rem',
        fontSize: '0.75rem',
      },
    },
  },
  css: ({ dt }) => `
    .p-button {
      line-height: ${dt('button.size.lineHeight')};
    }

    .p-button.p-button-sm {
      line-height: ${dt('button.size.sm.lineHeight')};
    }

    .p-button.p-button-lg {
      line-height: ${dt('button.size.lg.lineHeight')};
    }

    /* WITH BADGE */
    .p-button:has(.p-badge) {
      padding-block: calc(${dt('button.padding.y')} - 1px);
    }
    .p-button.p-button-sm:has(.p-badge) {
      padding-block: calc(${dt('button.sm.padding.y')} - 1px);
    }
    .p-button.p-button-lg:has(.p-badge) {
      padding-block: calc(${dt('button.lg.padding.y')} - 1px);
    }

    /* BADGE */
    .p-button .p-badge {
      font-size: ${dt('button.badge.fontSize')};
      border-radius: ${dt('button.badge.borderRadius')};
    }
    .p-button.p-button-sm .p-badge {
      min-width: ${dt('button.badge.sm.size')};
      height: ${dt('button.badge.sm.size')};
      font-size: ${dt('button.badge.sm.fontSize')};
    }

    /* DISABLED STATE */
    .p-button:disabled {
      background-color: color-mix(in srgb, ${dt('primary.950')} 12.5%, transparent);
      border: 1px solid transparent;
      color: ${dt('primary.950')};
    }
    .p-button.p-button-outlined:disabled {
      background-color: transparent;
      border: ${dt('primary.950')} 1px solid;
    }
    .p-button.p-button-text:disabled {
      background-color: transparent;
      border: none;
    }

    /* DISABLED STATE - DARK MODE */
    .slnk-primeng-dark .p-button:disabled {
      background-color: color-mix(in srgb, #ffffff 25%, transparent);
      color: #ffffff;
      border: transparent 1px solid;
    }
    .slnk-primeng-dark .p-button.p-button-outlined:disabled {
      background-color: transparent;
      border: #ffffff 1px solid;
    }
    .slnk-primeng-dark .p-button.p-button-text:disabled {
      background-color: transparent;
      border: none;
    }
  `,
  colorScheme: {
    light: {
      root: {
        primary: {
          background: '{primary.color}',
          hoverBackground: '{primary.hover.color}',
          hoverBorderColor: '{primary.hover.color}',
          hoverColor: '{primary.contrast.color}',
          activeBackground: '{primary.active.color}',
          activeBorderColor: '{primary.active.color}',
          activeColor: '{primary.contrast.color}',
          borderColor: '{primary.color}',
          color: '{primary.contrast.color}',
          focusRing: {
            color: '{primary.color}',
            shadow: 'none',
          },
        },
        secondary: {
          background: '{surface.100}',
          hoverBackground: '{surface.200}',
          hoverBorderColor: '{surface.200}',
          hoverColor: '{surface.700}',
          activeBackground: '{surface.300}',
          activeBorderColor: '{surface.300}',
          activeColor: '{surface.800}',
          borderColor: '{surface.100}',
          color: '{surface.600}',
          focusRing: {
            color: '{surface.600}',
            shadow: 'none',
          },
        },
        info: {
          background: '{sky.500}',
          hoverBackground: '{sky.600}',
          hoverBorderColor: '{sky.600}',
          hoverColor: '#ffffff',
          activeBackground: '{sky.700}',
          activeBorderColor: '{sky.700}',
          activeColor: '#ffffff',
          borderColor: '{sky.500}',
          color: '#ffffff',
          focusRing: {
            color: '{sky.500}',
            shadow: 'none',
          },
        },
        success: {
          background: '{green.500}',
          hoverBackground: '{green.600}',
          hoverBorderColor: '{green.600}',
          hoverColor: '#ffffff',
          activeBackground: '{green.700}',
          activeBorderColor: '{green.700}',
          activeColor: '#ffffff',
          borderColor: '{green.500}',
          color: '#ffffff',
          focusRing: {
            color: '{green.500}',
            shadow: 'none',
          },
        },
        warn: {
          background: '{orange.500}',
          hoverBackground: '{orange.600}',
          hoverBorderColor: '{orange.600}',
          hoverColor: '#ffffff',
          activeBackground: '{orange.700}',
          activeBorderColor: '{orange.700}',
          activeColor: '#ffffff',
          borderColor: '{orange.500}',
          color: '#ffffff',
          focusRing: {
            color: '{orange.500}',
            shadow: 'none',
          },
        },
        help: {
          background: '{purple.500}',
          hoverBackground: '{purple.600}',
          hoverBorderColor: '{purple.600}',
          hoverColor: '#ffffff',
          activeBackground: '{purple.700}',
          activeBorderColor: '{purple.700}',
          activeColor: '#ffffff',
          borderColor: '{purple.500}',
          color: '#ffffff',
          focusRing: {
            color: '{purple.500}',
            shadow: 'none',
          },
        },
        danger: {
          background: '{red.600}',
          hoverBackground: '{red.700}',
          hoverBorderColor: '{red.700}',
          hoverColor: '#ffffff',
          activeBackground: '{red.800}',
          activeBorderColor: '{red.800}',
          activeColor: '#ffffff',
          borderColor: '{red.600}',
          color: '#ffffff',
          focusRing: {
            color: '{red.500}',
            shadow: 'none',
          },
        },
        contrast: {
          background: '{surface.950}',
          hoverBackground: '{surface.900}',
          hoverBorderColor: '{surface.900}',
          hoverColor: '{surface.0}',
          activeBackground: '{surface.800}',
          activeBorderColor: '{surface.800}',
          activeColor: '{surface.0}',
          borderColor: '{surface.950}',
          color: '{surface.0}',
          focusRing: {
            color: '{surface.950}',
            shadow: 'none',
          },
        },
      },
      outlined: {
        primary: {
          hoverBackground: '{primary.50}',
          activeBackground: '{primary.50}',
          borderColor: '{primary.500}',
          color: '{primary.color}',
        },
        secondary: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{surface.500}',
        },
        success: {
          hoverBackground: '{green.50}',
          activeBackground: '{green.100}',
          borderColor: '{green.200}',
          color: '{green.500}',
        },
        info: {
          hoverBackground: '{sky.50}',
          activeBackground: '{sky.100}',
          borderColor: '{sky.200}',
          color: '{sky.500}',
        },
        warn: {
          hoverBackground: '{orange.50}',
          activeBackground: '{orange.100}',
          borderColor: '{orange.200}',
          color: '{orange.500}',
        },
        help: {
          hoverBackground: '{purple.50}',
          activeBackground: '{purple.100}',
          borderColor: '{purple.200}',
          color: '{purple.500}',
        },
        danger: {
          hoverBackground: '{red.50}',
          activeBackground: '{red.50}',
          borderColor: '{red.600}',
          color: '{red.600}',
        },
        contrast: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.700}',
          color: '{surface.950}',
        },
        plain: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{surface.700}',
        },
      },
      text: {
        primary: {
          hoverBackground: '{primary.50}',
          activeBackground: '{primary.50}',
          color: '{primary.color}',
        },
        secondary: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          color: '{surface.500}',
        },
        success: {
          hoverBackground: '{green.50}',
          activeBackground: '{green.100}',
          color: '{green.500}',
        },
        info: {
          hoverBackground: '{sky.50}',
          activeBackground: '{sky.100}',
          color: '{sky.500}',
        },
        warn: {
          hoverBackground: '{orange.50}',
          activeBackground: '{orange.100}',
          color: '{orange.500}',
        },
        help: {
          hoverBackground: '{purple.50}',
          activeBackground: '{purple.100}',
          color: '{purple.500}',
        },
        danger: {
          hoverBackground: '{red.50}',
          activeBackground: '{red.50}',
          color: '{red.600}',
        },
        plain: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          color: '{surface.700}',
        },
        contrast: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          color: '{surface.950}',
        },
      },
      link: {
        color: '{primary.color}',
        hoverColor: '{primary.color}',
        activeColor: '{primary.color}',
      },
    },
    dark: {
      root: {
        primary: {
          background: '{primary.color}',
          hoverBackground: '{primary.hover.color}',
          hoverBorderColor: '{primary.hover.color}',
          hoverColor: '{primary.contrast.color}',
          activeBackground: '{primary.active.color}',
          activeBorderColor: '{primary.active.color}',
          activeColor: '{primary.contrast.color}',
          borderColor: '{primary.color}',
          color: '{primary.contrast.color}',
          focusRing: {
            color: '{primary.color}',
            shadow: 'none',
          },
        },
        secondary: {
          background: '{surface.800}',
          hoverBackground: '{surface.700}',
          hoverBorderColor: '{surface.700}',
          hoverColor: '{surface.200}',
          activeBackground: '{surface.600}',
          activeBorderColor: '{surface.600}',
          activeColor: '{surface.100}',
          borderColor: '{surface.800}',
          color: '{surface.300}',
          focusRing: {
            color: '{surface.300}',
            shadow: 'none',
          },
        },
        info: {
          background: '{sky.400}',
          hoverBackground: '{sky.300}',
          hoverBorderColor: '{sky.300}',
          hoverColor: '{sky.950}',
          activeBackground: '{sky.200}',
          activeBorderColor: '{sky.200}',
          activeColor: '{sky.950}',
          borderColor: '{sky.400}',
          color: '{sky.950}',
          focusRing: {
            color: '{sky.400}',
            shadow: 'none',
          },
        },
        success: {
          background: '{green.400}',
          hoverBackground: '{green.300}',
          hoverBorderColor: '{green.300}',
          hoverColor: '{green.950}',
          activeBackground: '{green.200}',
          activeBorderColor: '{green.200}',
          activeColor: '{green.950}',
          borderColor: '{green.400}',
          color: '{green.950}',
          focusRing: {
            color: '{green.400}',
            shadow: 'none',
          },
        },
        warn: {
          background: '{orange.400}',
          hoverBackground: '{orange.300}',
          hoverBorderColor: '{orange.300}',
          hoverColor: '{orange.950}',
          activeBackground: '{orange.200}',
          activeBorderColor: '{orange.200}',
          activeColor: '{orange.950}',
          borderColor: '{orange.400}',
          color: '{orange.950}',
          focusRing: {
            color: '{orange.400}',
            shadow: 'none',
          },
        },
        help: {
          background: '{purple.400}',
          hoverBackground: '{purple.300}',
          hoverBorderColor: '{purple.300}',
          hoverColor: '{purple.950}',
          activeBackground: '{purple.200}',
          activeBorderColor: '{purple.200}',
          activeColor: '{purple.950}',
          borderColor: '{purple.400}',
          color: '{purple.950}',
          focusRing: {
            color: '{purple.400}',
            shadow: 'none',
          },
        },
        danger: {
          background: '{red.300}',
          hoverBackground: '{red.200}',
          hoverBorderColor: '{red.200}',
          hoverColor: '{red.950}',
          activeBackground: '{red.100}',
          activeBorderColor: '{red.100}',
          activeColor: '{red.950}',
          borderColor: '{red.300}',
          color: '{red.950}',
          focusRing: {
            color: '{red.300}',
            shadow: 'none',
          },
        },
        contrast: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          hoverBorderColor: '{surface.100}',
          hoverColor: '{surface.950}',
          activeBackground: '{surface.200}',
          activeBorderColor: '{surface.200}',
          activeColor: '{surface.950}',
          borderColor: '{surface.0}',
          color: '{surface.950}',
          focusRing: {
            color: '{surface.0}',
            shadow: 'none',
          },
        },
      },
      outlined: {
        primary: {
          hoverBackground: '{primary.900}',
          activeBackground: '{primary.900}',
          borderColor: '{primary.color}',
          color: '{primary.300}',
        },
        secondary: {
          hoverBackground: 'rgba(255, 255, 255, 0.04)',
          activeBackground: 'rgba(255, 255, 255, 0.16)',
          borderColor: '{surface.700}',
          color: '{surface.400}',
        },
        /**
         * @todo: Check if this is correct
         */
        success: {
          hoverBackground: 'rgba(NaN, NaN, 14, 0.04)',
          activeBackground: 'rgba(NaN, NaN, 14, 0.16)',
          borderColor: '{green.700}',
          color: '{green.400}',
        },
        info: {
          hoverBackground: 'rgba(NaN, NaN, NaN, 0.04)',
          activeBackground: 'rgba(NaN, NaN, NaN, 0.16)',
          borderColor: '{sky.700}',
          color: '{sky.400}',
        },
        warn: {
          hoverBackground: 'rgba(NaN, NaN, NaN, 0.04)',
          activeBackground: 'rgba(NaN, NaN, NaN, 0.16)',
          borderColor: '{orange.700}',
          color: '{orange.400}',
        },
        help: {
          hoverBackground: 'rgba(NaN, NaN, NaN, 0.04)',
          activeBackground: 'rgba(NaN, NaN, NaN, 0.16)',
          borderColor: '{purple.700}',
          color: '{purple.400}',
        },
        danger: {
          hoverBackground: '{red.900}',
          activeBackground: '{red.900}',
          borderColor: '{red.300}',
          color: '{red.400}',
        },
        contrast: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          borderColor: '{surface.500}',
          color: '{surface.0}',
        },
        plain: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          borderColor: '{surface.600}',
          color: '{surface.0}',
        },
      },
      text: {
        primary: {
          hoverBackground: '{primary.900}',
          activeBackground: '{primary.900}',
          color: '{primary.300}',
        },
        secondary: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          color: '{surface.400}',
        },
        success: {
          hoverBackground: 'rgba(NaN, NaN, 14, 0.04)',
          activeBackground: 'rgba(NaN, NaN, 14, 0.16)',
          color: '{green.400}',
        },
        info: {
          hoverBackground: 'rgba(NaN, NaN, NaN, 0.04)',
          activeBackground: 'rgba(NaN, NaN, NaN, 0.16)',
          color: '{sky.400}',
        },
        warn: {
          hoverBackground: 'rgba(NaN, NaN, NaN, 0.04)',
          activeBackground: 'rgba(NaN, NaN, NaN, 0.16)',
          color: '{orange.400}',
        },
        help: {
          hoverBackground: 'rgba(NaN, NaN, NaN, 0.04)',
          activeBackground: 'rgba(NaN, NaN, NaN, 0.16)',
          color: '{purple.400}',
        },
        danger: {
          hoverBackground: '{red.900}',
          activeBackground: '{red.900}',
          color: '{red.400}',
        },
        plain: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          color: '{surface.0}',
        },
        contrast: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          color: '{surface.0}',
        },
      },
      link: {
        color: '{primary.color}',
        hoverColor: '{primary.color}',
        activeColor: '{primary.color}',
      },
    },
  },
};
